import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Popup, Segment, Grid, Icon } from "../..";
import { getRest } from "../../utils";

/**
 * Get the color depending on type
 *
 * @param {*} type
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var getBorderLeftColor = function getBorderLeftColor(type) {
  switch (type) {
    case 'error':
    case 'important':
      return 'red';
    case 'warning':
      return 'yellow';
    case 'information':
      return 'olive';
    case '':
    default:
      return '';
  }
};

/**
 * Get icon name depending on type
 *
 * @param {*} type
 */
var getIconName = function getIconName(type) {
  switch (type) {
    case 'error':
    case 'important':
      return 'exclamation';
    case 'warning':
    case 'information':
    case '':
    default:
      return null;
  }
};

/**
 * Tooltip du framework 360.
 *
 * @param {*} props
 */
var Tooltip = function Tooltip(props) {
  var children = props.children,
    className = props.className,
    content = props.content,
    trigger = props.trigger,
    message = props.message,
    description = props.description,
    type = props.type,
    position = props.position,
    icon = props.icon,
    mouseEnterDelay = props.mouseEnterDelay;
  var rest = getRest(Tooltip, props);
  var hasMessage = !_.isNil(message) && !_.isEmpty(message);
  var hasDescription = !_.isNil(description) && !_.isEmpty(description);
  var iconName = !_.isNil(icon) ? icon : getIconName(type);
  var borderColor = getBorderLeftColor(type);
  var borderLeftStyle = {
    marginRight: 0,
    marginLeft: 0,
    borderRadius: '4px 0 0 4px',
    width: '22px'
  };
  var classes = classNames('tooltip', className);
  if (!_.isNil(children) || !_.isEmpty(children)) {
    return /*#__PURE__*/_jsx(Popup, Object.assign({}, rest, {
      position: position,
      className: classes,
      mouseEnterDelay: mouseEnterDelay,
      trigger: /*#__PURE__*/React.cloneElement(trigger, {
        error: type === 'error' ? true : null
      }),
      children: children
    }));
  }

  // cas content on gère quand même le trigger qui devient rouge en cas d'erreur
  if (!(_.isNil(content) || _.isEmpty(content))) {
    return /*#__PURE__*/_jsx(Popup, Object.assign({}, rest, {
      position: position,
      className: classes,
      content: content,
      style: {
        padding: 0
      },
      mouseEnterDelay: mouseEnterDelay,
      trigger: /*#__PURE__*/React.cloneElement(trigger, {
        error: type === 'error' ? true : null
      })
    }));
  }

  // Si pas de message et pas de description, on affiche juste le trigger
  if (!hasMessage && !hasDescription) {
    return /*#__PURE__*/React.cloneElement(trigger, {
      error: type === 'error' ? true : null
    });
  }

  // cas message + description
  if (hasMessage && hasDescription) {
    return /*#__PURE__*/_jsx(Popup, Object.assign({}, rest, {
      position: position,
      className: classes,
      style: {
        padding: 0
      },
      mouseEnterDelay: mouseEnterDelay,
      trigger: /*#__PURE__*/React.cloneElement(trigger, {
        error: type === 'error' ? true : null
      }),
      content: /*#__PURE__*/_jsx(Grid, {
        padded: true,
        children: /*#__PURE__*/_jsxs(Grid.Row, {
          style: {
            flexWrap: 'nowrap'
          },
          children: [type !== '' && /*#__PURE__*/_jsx(Grid.Column, {
            as: Segment,
            textAlign: "center",
            style: Object.assign({
              padding: '1em 0'
            }, borderLeftStyle),
            color: borderColor,
            children: !_.isNil(iconName) && /*#__PURE__*/_jsx(Icon, {
              fitted: true,
              name: iconName,
              style: {
                fontStyle: 'italic'
              }
            })
          }), /*#__PURE__*/_jsxs(Grid.Column, {
            as: Segment,
            verticalAlign: "middle",
            basic: true,
            width: 16,
            style: {
              padding: 0
            },
            children: [/*#__PURE__*/_jsx(Segment, {
              basic: true,
              style: {
                paddingTop: 0,
                paddingBottom: 0
              },
              children: message
            }), /*#__PURE__*/_jsx(Segment, {
              basic: true,
              style: {
                paddingTop: 0,
                paddingBottom: 0
              },
              children: /*#__PURE__*/_jsx("i", {
                children: description
              })
            })]
          })]
        })
      })
    }));
  }

  // seulement message ou seulement description
  return /*#__PURE__*/_jsx(Popup, Object.assign({}, rest, {
    position: position,
    className: classes,
    style: {
      padding: 0
    },
    mouseEnterDelay: mouseEnterDelay,
    trigger: /*#__PURE__*/React.cloneElement(trigger, {
      error: type === 'error' ? true : null
    }),
    content: /*#__PURE__*/_jsx(Grid, {
      padded: true,
      children: /*#__PURE__*/_jsxs(Grid.Row, {
        style: {
          flexWrap: 'nowrap'
        },
        children: [type !== '' && /*#__PURE__*/_jsx(Grid.Column, {
          as: Segment,
          textAlign: "center",
          style: Object.assign({
            padding: '1em 0'
          }, borderLeftStyle),
          color: borderColor,
          children: !_.isNil(iconName) && /*#__PURE__*/_jsx(Icon, {
            fitted: true,
            name: iconName,
            style: {
              fontStyle: 'italic'
            }
          })
        }), /*#__PURE__*/_jsx(Grid.Column, {
          as: Segment,
          verticalAlign: "middle",
          basic: true,
          width: 16,
          style: {
            padding: 0
          },
          children: /*#__PURE__*/_jsx(Segment, {
            basic: true,
            style: {
              paddingTop: 0,
              paddingBottom: 0
            },
            children: hasDescription ? /*#__PURE__*/_jsx("i", {
              children: description
            }) : message
          })
        })]
      })
    })
  }));
};
Tooltip.handledProps = ["children", "className", "content", "description", "icon", "message", "mouseEnterDelay", "position", "trigger", "type"];
Tooltip.propTypes = {
  /** Primary content. */
  children: PropTypes.node,
  /** Additional classes. */
  className: PropTypes.string,
  /** Shorthand for primary content. */
  content: PropTypes.node,
  /** Texte secondaire, en italique. */
  description: PropTypes.string,
  /** Name of displayed icon. */
  icon: PropTypes.string,
  /** Texte principal. */
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** Position par rapport au trigger. */
  position: PropTypes.oneOf(['top right', 'top center', 'top left', 'bottom right', 'bottom center', 'bottom left', 'left center']),
  /** Element to be rendered in-place where the tooltip is defined. */
  trigger: PropTypes.node.isRequired,
  /** Type */
  type: PropTypes.oneOf(['', 'error', 'information', 'warning', 'important']),
  /** Milliseconds to wait before opening on mouse over for the tooltip. */
  mouseEnterDelay: PropTypes.number
};
Tooltip.defaultProps = {
  type: '',
  position: 'bottom left',
  mouseEnterDelay: 1000
};
export default Tooltip;